import { defineStore } from "pinia";
import { watch } from "vue";
import router from "@/router";
import LocationsService from "@/services/LocationsService";
import { useAuthStore } from "@/stores/auth.store";
import { useCurrentStore } from "@/stores/current";
import { useCurrentFiltersStore } from "@/stores/currentFilters";
import { useArchivalStore } from "@/stores/archival";
import { useArchivalFiltersStore } from "@/stores/archivalFilters";
import { useStatisticStore } from "@/stores/statistic";
import { useStatisticFiltersStore } from "@/stores/statisticFilters";

/**
 * Ewidencja czasu korzystania ze stacji preselekcji (wymóg OPZ / GDDKiA).
 *
 * Backend zapisuje w rejestrze aktywności okresy, w których użytkownik obserwował dane konkretnej stacji.
 * Sam nie wie jednak, kiedy użytkownik przestał je obserwować — cofnięcie się do wcześniejszego kroku kreatora
 * nie generuje żadnego żądania. Dlatego stan wysyła front: po każdej zmianie leci `PUT /locations/login`
 * z pełnym zbiorem obserwowanych stacji, a backend otwiera i zamyka wpisy tak, żeby go odwzorować.
 *
 * Obserwowanie zaczyna się z chwilą wejścia na krok z wynikami — niezależnie od tego, czy wyszukiwanie
 * cokolwiek zwróciło — i kończy z chwilą opuszczenia tego kroku.
 *
 * Zbiór jest sumą wszystkich źródeł naraz, nie stanem pojedynczego widoku, bo jedna sesja może obserwować
 * kilka rzeczy jednocześnie. Archiwum i statystyki pokazują wynik zamrożony w czasie, więc liczą się tylko
 * dopóki użytkownik patrzy na ich zakładkę. Podgląd bieżących pomiarów odpytuje API również po przejściu
 * gdzie indziej (interwał nie jest zatrzymywany przy opuszczeniu widoku), więc trwa niezależnie od trasy.
 */

/** Opóźnienie wysyłki [ms] — skleja serie zmian (np. odznaczanie kolejnych stacji) w jedno żądanie. */
const DEBOUNCE_MS = 250;

/** Nazwy tras widoku szczegółów pomiaru; parametr `location` wskazuje stację, której dane są oglądane. */
const VEHICLE_DETAILS_ROUTES = ["VehicleDetails", "VehicleArchDetails"];

/**
 * Trasy, na których wyniki archiwum są nadal obserwowane. Widok szczegółów pomiaru otwierany z listy jest
 * jej przedłużeniem, a nie opuszczeniem — bez tego wejście w pojedynczy pomiar zamykałoby wpisy pozostałych
 * stacji z filtra i otwierało je na nowo po powrocie, zaśmiecając rejestr.
 */
const ARCHIVAL_ROUTES = ["archival", "result", "VehicleArchDetails"];

/** Trasa, na której obserwowane są wyniki statystyk. */
const STATISTIC_ROUTES = ["statistics"];

/**
 * @param {unknown} value
 * @returns {number[]} Identyfikatory stacji sprowadzone do unikalnych, dodatnich liczb.
 */
function toLocationIds(value) {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map(Number).filter((id) => Number.isInteger(id) && id > 0);
}

export const useLocationSessionStore = defineStore({
  id: "locationSession",
  state: () => ({
    /** @type {string|null} Ostatnio wysłany zbiór (posortowany, jako klucz) — null dopóki nic nie wysłano. */
    lastSentKey: null,
    /** @type {number|undefined} Uchwyt opóźnionej wysyłki. */
    debounceTimer: undefined,
    /** @type {boolean} Czy nasłuch został już uruchomiony (chroni przed podwójną rejestracją). */
    watching: false,
  }),

  actions: {
    /**
     * Zbiór stacji preselekcji, których dane użytkownik w tej chwili obserwuje.
     *
     * @returns {number[]}
     */
    observedLocationIds() {
      const ids = new Set();
      const routeName = router.currentRoute.value?.name;
      const dodaj = (locations) => toLocationIds(locations).forEach((id) => ids.add(id));

      // Bieżące pomiary — krok 3 to widok wyników. Niezależnie od aktywnej zakładki: interwał odpytujący
      // API nie jest zatrzymywany przy opuszczeniu widoku, więc pomiary są zbierane dalej.
      if (useCurrentStore().step === 3) {
        dodaj(useCurrentFiltersStore().filters?.locations);
      }

      // Archiwum — krok 4. Wyniki są statyczne, więc liczą się tylko dopóki użytkownik na nie patrzy.
      if (useArchivalStore().isResult && ARCHIVAL_ROUTES.includes(routeName)) {
        dodaj(useArchivalFiltersStore().filters?.locations);
      }

      // Statystyki — `isResult` (numer kroku wraca po wygenerowaniu wykresu do 1).
      if (useStatisticStore().isResult && STATISTIC_ROUTES.includes(routeName)) {
        dodaj(useStatisticFiltersStore().filters?.locations);
      }

      // Szczegóły pojedynczego pomiaru — użytkownik ogląda dane stacji, która go zarejestrowała.
      if (VEHICLE_DETAILS_ROUTES.includes(routeName)) {
        dodaj([router.currentRoute.value.params?.location]);
      }

      return [...ids].sort((a, b) => a - b);
    },

    /**
     * Wysyła stan do backendu, jeżeli różni się od ostatnio wysłanego.
     *
     * Błąd jest tłumiony świadomie: ewidencja jest funkcją audytową działającą w tle i nie może przerwać
     * pracy użytkownika ani zasypać go komunikatami. Nieudana wysyłka nie zostaje zapamiętana jako stan
     * ostatnio wysłany, więc najbliższa zmiana kroku spróbuje ponownie.
     */
    async push() {
      if (!useAuthStore().token) {
        return;
      }

      const ids = this.observedLocationIds();
      const key = ids.join(",");

      if (key === this.lastSentKey) {
        return;
      }

      // Zapisujemy przed wysyłką, żeby równoległe wywołania nie powieliły tego samego żądania.
      const previousKey = this.lastSentKey;
      this.lastSentKey = key;

      try {
        await LocationsService.syncLoginToLocations(ids);
      } catch (error) {
        this.lastSentKey = previousKey;
        console.error("Nie udało się zapisać ewidencji dostępu do stacji:", error);
      }
    },

    /** Planuje wysyłkę, sklejając serię zmian w jedno żądanie. */
    schedulePush() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.push(), DEBOUNCE_MS);
    },

    /**
     * Uruchamia nasłuch zmian kroków, filtrów lokalizacji i trasy.
     *
     * Pierwsza wysyłka jest bezwarunkowa: po odświeżeniu strony store'y startują od kroku 1, a sesja backendu
     * może jeszcze trzymać otwarte wpisy z poprzedniego przebiegu — trzeba je zamknąć.
     */
    start() {
      if (this.watching) {
        return;
      }
      this.watching = true;

      watch(() => this.observedLocationIds().join(","), () => this.schedulePush());

      this.push();
    },

    /** Zeruje pamięć ostatnio wysłanego stanu (wylogowanie — sesja backendu zamyka wpisy sama). */
    reset() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = undefined;
      this.lastSentKey = null;
    },
  },
});
